import { useEffect, useRef, useState } from 'react';
import { getMeasurementsIssues } from '../../../shared/services/page-issues/getMeasurementsIssues.ts';
import { NavBar, NavBarRef } from '../../components/navBar.tsx';
import { PageTitle } from '../../components/pageTitle.tsx';
import { ProgramNameMappingDialog } from '../../components/program-name-mapping/programNameMappingDialog.tsx';
import { useStickyError } from '../../hooks/useStickyError.ts';
import { MeasureIntervalState, Measurement, Record } from '../../types/sharedTypeImpl.ts';
import { openLinkInNewTab } from '../../utils/navigation.ts';
import { trpc, trpcClient } from '../../utils/trpc/trpc.ts';
import { BatchDetailsDialog } from './batchDetailsDialog.tsx';
import { measureIntervalSort } from './measureIntervalSort.ts';
import { MeasureIntervalTable } from './measureIntervalTable.tsx';
import { MeasurementDialog } from './measurement-dialog/measurementDialog.tsx';


export function MeasureInterval() {
    const navBarRef = useRef<NavBarRef>(null);
    const [pendingMeasurementRow, setPendingMeasurementRow] = useState<MeasureIntervalState | null>(null);
    const [pendingBatchDetailsRow, setPendingBatchDetailsRow] = useState<MeasureIntervalState | null>(null);
    const [showMappingsDialog, setShowMappingsDialog] = useState(false);

    const isDialogOpen = pendingMeasurementRow != null || pendingBatchDetailsRow != null || showMappingsDialog;


    const { data, error, isLoading, refetch } = trpc.measureInterval.getTable.useQuery(undefined, {
        refetchInterval: 1_000,
        enabled: !isDialogOpen,
        select: (data) => measureIntervalSort([...data]),
    });
    const stickyError = useStickyError({ error, isLoading });

    // Update navbar issues, if issue count changes
    useEffect(() => {
        if (data == null) return;
        const numIssues = getMeasurementsIssues(data);
        navBarRef.current?.refetchIfMeasurementsMismatch(numIssues);
    }, [data]);

    function handlePartCountClicked(row: MeasureIntervalState) {
        if (!row.equipment || (!row.inOfflineMode && !row.product)) {
            return;
        }

        setPendingBatchDetailsRow(row);
    }

    function handleMeasureClicked(row: MeasureIntervalState) {
        if (!row.equipment || (!row.inOfflineMode && !row.product)) {
            return;
        }

        setPendingMeasurementRow(row);
    }

    async function handleEndSetupClicked(row: MeasureIntervalState) {
        const confirmed = confirm(`End setup of ${row.displayName} - ${row.partDisplayName}?`);
        if (confirmed) {
            try {
                await trpcClient.measureInterval.endSetup.mutate({ equipment: row.equipment });
            } catch (error) {
                console.error('Error ending setup:', error);
                alert('Failed to end setup.');
            }
        }
    }

    async function handleMeasurementAdded(measurement: Measurement, record: Record | null) {
        setPendingMeasurementRow(null);
        refetch();

        if (record != null) {
            openLinkInNewTab(`record-view`, { id: record._id, mode: 'edit' });
        }
    }


    return (
        <>
            <PageTitle title='Measurement' />
            <NavBar ref={navBarRef} />
            <div className='content' style={{ marginTop: '12px' }}>
                <div className='flex--horz'>
                    {stickyError && <p className='page-error'>{stickyError?.message}</p>}
                    <div className='flex__spacer' />
                    <button style={{ marginBottom: '8px' }} onClick={() => setShowMappingsDialog(true)} children='Show mappings' />
                </div>

                <MeasureIntervalTable
                    data={data ?? []}
                    onPartCountClicked={handlePartCountClicked}
                    onMeasureClicked={handleMeasureClicked}
                    onEndSetupClicked={handleEndSetupClicked}
                />
            </div>
            {pendingMeasurementRow && (
                <MeasurementDialog
                    equipment={pendingMeasurementRow?.equipment}
                    equipmentDisplayName={pendingMeasurementRow?.displayName}
                    product={pendingMeasurementRow?.product}
                    partCount={pendingMeasurementRow?.partCount}
                    targetQuantity={pendingMeasurementRow?.targetQuantity}
                    inOfflineMode={pendingMeasurementRow?.inOfflineMode}
                    protocolId={pendingMeasurementRow?.protocolId}
                    onMeasurementAdded={handleMeasurementAdded}
                    onClose={() => setPendingMeasurementRow(null)}
                />
            )}
            {pendingBatchDetailsRow && (
                <BatchDetailsDialog measureIntervalRow={pendingBatchDetailsRow} onClose={() => setPendingBatchDetailsRow(null)} />
            )}

            {showMappingsDialog && <ProgramNameMappingDialog onClose={() => setShowMappingsDialog(false)} />}
        </>
    );
}
